import { type ClientContext, ContextClient } from '~/services/contextClient';
import ThreadsClientFactory from './threadsClient.factory';
import type {
    ThreadsClientInterface,
    Thread,
    ThreadsSummary,
    ThreadsGetParams,
    AssignPayload,
    CheckpointResponse,
    ThreadCountsResponse,
    SnoozePayload,
} from './threadsClient.types';
import { threadsClientConfig } from './threadsClient.types';
import type { CursorPaginationResponse } from '~/services/api';
import type { AxiosPromise } from 'axios';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';
import BaseClient from '~/services/BaseClient';

class ThreadsClient extends ContextClient<ThreadsClientInterface> {
    private baseClient: BaseClient;

    constructor() {
        super(ThreadsClientFactory);
        this.baseClient = new BaseClient(threadsClientConfig);
    }

    getThreads(
        context: ClientContext,
        params?: ThreadsGetParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<Thread>> {
        const client = this.getClient(context);
        return client.getThreads(params, config);
    }

    getSummary(context: ClientContext, config?: ExtendedAxiosRequestConfig): AxiosPromise<ThreadsSummary> {
        const client = this.getClient(context);
        return client.getSummary(config);
    }

    setState(context: ClientContext, contactId: number, state: string, groupId?: string): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.setState(contactId, state, groupId);
    }

    assignToSelf(context: ClientContext, contactId: number, groupId?: string): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.assignToSelf(contactId, groupId);
    }

    assignTo(context: ClientContext, contactId: number, payload: AssignPayload, groupId?: string): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.assignTo(contactId, payload, groupId);
    }

    unclaim(context: ClientContext, contactId: number, groupId?: string): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.unclaim(contactId, groupId);
    }

    checkpointSave(context: ClientContext, contactId: number, groupId?: string): AxiosPromise<CheckpointResponse> {
        const client = this.getClient(context);
        return client.checkpointSave(contactId, groupId);
    }

    checkpointDelete(context: ClientContext, contactId: number, groupId?: string): AxiosPromise<CheckpointResponse> {
        const client = this.getClient(context);
        return client.checkpointDelete(contactId, groupId);
    }

    checkpointGet(context: ClientContext, contactId: number, groupId?: string): AxiosPromise<CheckpointResponse> {
        const client = this.getClient(context);
        return client.checkpointGet(contactId, groupId);
    }

    getCounts(context: ClientContext, config?: ExtendedAxiosRequestConfig): AxiosPromise<ThreadCountsResponse> {
        const client = this.getClient(context);
        if (!client.getCounts) {
            throw new Error(`getCounts not supported by ${client.constructor.name}`);
        }
        return client.getCounts(config);
    }

    snooze(context: ClientContext, contactId: number, payload: SnoozePayload): AxiosPromise<void> {
        const client = this.getClient(context);
        if (!client.snooze) {
            throw new Error(`snooze not supported by ${client.constructor.name}`);
        }
        return client.snooze(contactId, payload);
    }

    complete(context: ClientContext, contactId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        if (!client.complete) {
            throw new Error(`complete not supported by ${client.constructor.name}`);
        }
        return client.complete(contactId);
    }

    getThreadsUsingLink(link: string): AxiosPromise<CursorPaginationResponse<Thread>> {
        return this.baseClient.makeCoreApiRequest(link);
    }
}

export default new ThreadsClient();
